import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntity, WEB_BROWSER_MODEL_TIERS } from "@erp/shared-types";
import { entityOfType } from "../../utils/system-entities";
import { VisibilityPredicateType } from "../../registry/visibility";
import { ServerInfrastructureConfigKey } from "../../registry/visibility";

export const webBrowserStep = defineStepConfig({
  type: "web_browser",
  name: "Browser Use Agent",
  description:
    "Opens a web browser and executes AI-driven instructions. Configure dynamic data inputs, a prompt with {{key}} interpolation, credentials, and a starting URL.",
  category: "AI & Agents",
  visibility: [
    [
      {
        type: VisibilityPredicateType.ServerInfrastructureConfig,
        key: ServerInfrastructureConfigKey.BrowserUse,
      },
    ],
  ],

  inputSchema: z.object({}).passthrough(),

  outputSchema: z
    .object({
      success: z.boolean(),
      message: z.string().nullable(),
      error: z.string().nullable(),
      iterationsUsed: z.number().nullable(),
      downloads: z
        .array(
          z.object({
            documentId: z.string(),
            fileName: z.string(),
            fileSizeBytes: z.number(),
            mimeType: z.string(),
          }),
        )
        .default([])
        .describe(
          "Files the operator downloaded during the session, each captured as a Document. Populated when download capture is enabled (OPERATOR_BROWSER_USE_DOWNLOADS_ENABLED, on by default) for Kernel operator sessions; empty otherwise.",
        ),
      documentIds: z
        .array(z.string())
        .default([])
        .describe(
          "Flat list of downloaded Document IDs (mirrors downloads[].documentId). Wire into send_email's documentIds input to attach the operator's downloaded files.",
        ),
      file: z
        .array(entityOfType(SystemEntity.CORE_FILE))
        .default([])
        .describe(
          "Underlying File for each download, index-aligned with `downloads`/`documentIds` — wire this into File-typed downstream steps (e.g. split_excel_sheets_v4, unzip_file) instead of re-uploading.",
        ),
      structuredResult: z
        .record(z.string(), z.unknown())
        .nullable()
        .default(null)
        .describe(
          "Complete validated object returned by the operator when an output schema is configured.",
        ),
    })
    // A configured extraction schema adds collision-safe `field:<name>` outputs.
    .catchall(z.unknown()),

  configSchema: z
    .object({
      dataInputs: z
        .array(
          z.object({
            key: z
              .string()
              .regex(
                /^[a-z][a-z0-9_]*$/,
                "Key must be lowercase alphanumeric with underscores",
              ),
            label: z.string().optional(),
          }),
        )
        .default([])
        .superRefine((inputs, ctx) => {
          const seen = new Set<string>();
          inputs.forEach((input, index) => {
            if (seen.has(input.key)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate dataInputs[].key: ${input.key}`,
                path: [index, "key"],
              });
              return;
            }
            seen.add(input.key);
          });
        })
        .describe(
          "Dynamic data input sockets. Each becomes an input the user can wire. Reference values in the prompt with {{key}}.",
        ),
      prompt: z
        .string()
        .default("")
        .describe(
          "Instruction prompt for the browser operator. Use {{key}} to interpolate data input values. Required unless `sop` is set (a procedure run needs no prompt).",
        ),
      sop: z
        .object({
          sopId: z.string().uuid(),
          parameters: z
            .record(z.string(), z.string())
            .default({})
            .describe(
              "Values for the procedure's declared parameters. Use {{key}} to interpolate data input values.",
            ),
        })
        .optional()
        .describe(
          "Procedure-runner mode: replay this saved procedure step-by-step instead of running the agentic loop. Steps that drifted are repaired by the agentic healer mid-replay. Downloads and structured output are not produced in this mode.",
        ),
      procedures: z
        .array(z.string().uuid())
        .default([])
        .describe(
          "Procedures the operator may consult (`list_procedures`/`get_procedure`) or replay by name (`run_procedure`) while it works. Procedures that declare required parameters are skipped — pin those with `sop` instead. Agentic mode only.",
        ),
      outputSchemaDefinitionId: z
        .string()
        .uuid()
        .optional()
        .describe(
          "Extraction schema definition whose fields constrain the operator result and define typed output sockets.",
        ),
      outputSchemaDefinitionVersionId: z
        .string()
        .uuid()
        .optional()
        .describe("Pinned immutable extraction schema version."),
      startUrl: z
        .string()
        .url()
        .optional()
        .describe("URL to navigate to when the browser session starts"),
      credentials: z
        .array(
          z.object({
            credentialId: z.string(),
            label: z.string().default(""),
          }),
        )
        .default([])
        .transform((entries) =>
          entries.flatMap((e) => {
            if (!e.credentialId) return [];
            return [
              {
                ...e,
                credentialId: z.string().uuid().parse(e.credentialId),
              },
            ];
          }),
        )
        .describe("Application credentials available to the browser operator"),
      otpTargetId: z
        .string()
        .optional()
        .describe(
          "OTP target (TOTP secret or the org's SMS number, registered in Settings → Organization → OTP) for an MFA challenge the operator meets IN THE BROWSER. Set it and the operator gains a `fill_otp_code` tool that resolves a live code and types it without ever seeing it. Unset → no MFA tool is offered. Distinct from remoteDesktopLogin.otpTargetId, which clears the Azure challenge on the AVD sign-in.",
        ),
      timeoutSeconds: z
        .number()
        .int()
        .min(10)
        .max(3600)
        .optional()
        .describe(
          "Wall-clock budget for ONE operator turn. Unset → 300s (5 minutes). Raise it when a single turn legitimately runs longer — a portal login with an MFA challenge, or a slow download — since hitting it fails the run with reason=timeout. Bounds elapsed time, unlike maxIterations, which bounds step count.",
        ),
      model: z
        .enum(WEB_BROWSER_MODEL_TIERS)
        .default("gpt")
        .describe("AI model tier for the browser operator"),
      headless: z
        .boolean()
        .default(false)
        .describe(
          "Run the browser headlessly. Only affects local Playwright sessions (no Kernel/CDP provider configured) — hosted sessions manage their own visibility.",
        ),
      credentialSetId: z
        .string()
        .uuid()
        .optional()
        .describe(
          "Credential (by reference) for the operator's portal/app sign-in (e.g. the AVD / Microsoft login). Consumed by the remote-desktop login below; the value is resolved server-side and never leaves the backend.",
        ),
      typingDelayMs: z
        .number()
        .int()
        .min(0)
        .max(1000)
        .default(0)
        .describe(
          "Delay (ms) between keystrokes. 0 = instant/robotic (default); ~40 = human-like anti-bot pacing.",
        ),
      mouseSmooth: z
        .boolean()
        .default(false)
        .describe(
          "Glide the cursor along a human-like path on move steps (anti-bot). Off (default) = instant.",
        ),
      mouseDurationMs: z
        .number()
        .int()
        .min(0)
        .max(5000)
        .optional()
        .describe(
          "Duration (ms) of a smooth cursor move. Only used when mouseSmooth is on; omit to let the provider time it by distance.",
        ),
      snapCursorBeforeClick: z
        .boolean()
        .default(true)
        .describe(
          "Snap the cursor onto the target before each click so it doesn't glide there (the provider moves the cursor server-side on click). On (default) = instant clicks; off = human-like glide-to-click.",
        ),
      scrollPixelsPerUnit: z
        .number()
        .int()
        .min(1)
        .max(1000)
        .default(100)
        .describe(
          "Distance in pixels that one unit of a model scroll action travels. 100 (default) matches historical behavior; lower for finer scrolling.",
        ),
      remoteDesktopLogin: z
        .object({
          portalUrl: z
            .string()
            .url()
            .describe("AVD / Windows-365 portal URL to sign in through"),
          desktopName: z
            .string()
            .min(1)
            .describe("Name of the desktop tile to open after sign-in"),
          vmCredentialSetId: z
            .string()
            .uuid()
            .optional()
            .describe(
              "Credential (by reference) for the in-session VM / RDP password prompt",
            ),
          otpTargetId: z
            .string()
            .optional()
            .describe(
              "OTP target id for the Azure MFA challenge (resolved live)",
            ),
          desktopLoadWaitSeconds: z.number().min(0).max(120).optional(),
        })
        .optional()
        .describe(
          "When set, the Python operator logs into an AVD / Windows-365 desktop ITSELF before running the prompt (portal login uses credentialSetId). Leave unset for a normal web-browser run.",
        ),
    })
    .superRefine((config, ctx) => {
      const hasDefinition = config.outputSchemaDefinitionId !== undefined;
      const hasVersion = config.outputSchemaDefinitionVersionId !== undefined;
      if (hasDefinition !== hasVersion) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Browser output schema definition and version must be configured together",
          path: [
            hasDefinition
              ? "outputSchemaDefinitionVersionId"
              : "outputSchemaDefinitionId",
          ],
        });
      }

      // Procedure-runner mode replays authored steps deterministically — there is no
      // agentic loop to prompt, no model-authored structured result, and the procedure's
      // own login config (not the step's) is what arms the executor. Reject the
      // combinations that would be silently ignored rather than half-honoring them.
      if (!config.sop) {
        if (config.prompt.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Prompt is required unless a procedure is selected",
            path: ["prompt"],
          });
        }
        return;
      }

      if (hasDefinition || hasVersion) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Structured output is not available when running a procedure — a replay returns no model-authored result",
          path: ["outputSchemaDefinitionId"],
        });
      }
      if (config.remoteDesktopLogin) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Remote-desktop login is not configurable when running a procedure — the procedure's own login config is used",
          path: ["remoteDesktopLogin"],
        });
      }
      if (config.procedures.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Consultable procedures require the agentic loop — clear them, or clear the procedure being run",
          path: ["procedures"],
        });
      }
    }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
